import React, { useState, useEffect } from "react";
import { Edit } from "lucide-react";
import { toast } from "react-hot-toast";
import { financialAPI } from "../../../services/AdminPortal/financialInfoAPI"; // ✅ create this file similar to evaluationAPI

const FinancialDetails = ({ loanId, application }) => {
  const customerId = application?.user?._id;
  const [financialData, setFinancialData] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const defaultData = {
    creditScore: "",
    creditLimit: "",
    activeLoans:"",
    monthlyIncome: "",
    businessName: "",
    businessType: "",
    yearsInBusiness: "",
    existingEMIs: "",
  };

  const formatCurrency = (value) => (!value ? "-" : `₹${value}`);
  const formatText = (value) => (!value ? "-" : value);

  // ✅ Fetch existing Financial data
  useEffect(() => {
    const fetchFinancialData = async () => {
      if (!loanId) return;
      try {
        setLoading(true);
        const res = await financialAPI.getFinancialByLoanId(loanId);
        if (res.success && res.data) {
          const merged = { ...defaultData, ...res.data };
          setFinancialData(merged);
          setFormData(merged);
        } else {
          setFinancialData(defaultData);
        }
      } catch (err) {
        console.error("Error fetching financial data:", err);
        toast.error("Failed to load financial information");
        setError("Failed to load financial data");
      } finally {
        setLoading(false);
      }
    };
    fetchFinancialData();
  }, [loanId]);

  const openEditDialog = () => {
    setFormData(financialData || defaultData);
    setIsEditOpen(true);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveFinancialData = async () => {
    try {
      const res = await financialAPI.saveFinancialData(
        loanId,
        customerId,
        formData
      );
      if (res.success) {
        toast.success("Financial Information updated successfully!");
        setFinancialData(formData);
        setIsEditOpen(false);
      } else {
        toast.error("Failed to save Financial Information");
      }
    } catch (err) {
      console.error("Error saving financial data:", err);
      toast.error(`${err}`);
    }
  };

  if (loading)
    return (
      <div className="bg-[#131313] rounded-lg p-4 text-white text-center">
        Loading Financial Information...
      </div>
    );

  if (error)
    return (
      <div className="bg-[#131313] rounded-lg p-4 text-red-500 text-center">
        {error}
      </div>
    );

  const data = financialData || defaultData;

  return (
    <div className="bg-[#131313] rounded-lg p-4 text-white">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg">Financial Information</h3>
        <button
          onClick={openEditDialog}
          className="text-[#FFCA20] flex items-center gap-1 hover:text-yellow-300"
        >
          <Edit className="w-4 h-4" /> Edit Financial Info
        </button>
      </div>

      {/* Display Financial Info */}
      <div className="space-y-4">
        <div className="flex divide-x gap-2 divide-[#333333]">
          <div className="pr-2">
            <p className="text-gray-400 text-sm">Credit Score</p>
            <p className="text-green-500">{formatText(data.creditScore)}</p>
          </div>
          <div className="pl-2">
            <p className="text-gray-400 text-sm">Credit Limit</p>
            <p className="text-[#FFCA20]">{formatCurrency(data.creditLimit)}</p>
          </div>
          <div className="pl-2">
            <p className="text-gray-400 text-sm">Active Loans</p>
            <p className="text-[#FFCA20]">{formatCurrency(data.activeLoans)}</p>
          </div>
        

        </div>
        <div>
          <p className="text-gray-400 text-sm">Monthly Income</p>
          <p className="text-white">{formatCurrency(data.monthlyIncome)}</p>
        </div>
        <div>
          <p className="text-gray-400 text-sm">Business Name</p>
          <p className="text-white">{formatText(data.businessName)}</p>
        </div>
        <div className="flex">
          <div className="w-1/2">
            <p className="text-gray-400 text-sm">Business Type</p>
            <p className="text-white">{formatText(data.businessType)}</p>
          </div>
          <div className="w-1/2">
            <p className="text-gray-400 text-sm">Years in Business</p>
            <p className="text-white">{formatText(data.yearsInBusiness)}</p>
          </div>
        </div>
        <div>
          <p className="text-gray-400 text-sm">Existing EMI's</p>
          <p className="text-white">{formatText(data.existingEMIs)}</p>
        </div>
      </div>

      {/* ✅ Edit Modal */}
      {isEditOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-[#1e1e1e] p-6 rounded-lg w-[90%] max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl mb-4 text-[#FFCA20] font-semibold">
              Edit Financial Information
            </h2>

            {Object.entries(formData).map(([field, value]) => (
              <div key={field} className="mb-3">
                <label className="block text-sm text-gray-400 mb-1 capitalize">
                  {field.replace(/([A-Z])/g, " $1")}
                </label>
                <input
                  type="text"
                  value={value || ""}
                  onChange={(e) => handleChange(field, e.target.value)}
                  className="w-full bg-[#2c2c2c] text-white rounded p-2 focus:outline-none"
                />
              </div>
            ))}

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setIsEditOpen(false)}
                className="bg-gray-600 px-4 py-2 rounded hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={saveFinancialData}
                className="bg-[#FFCA20] text-black px-4 py-2 rounded font-semibold hover:bg-yellow-400"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialDetails;
